import { Panel } from "./Surface";
import { Tags, VerifiedPill } from "./HeroDetail";
import { Icon } from "../app/Icon";
import { labelize } from "../lib/format";
import { rateLabel } from "../lib/loads";

/** Right-rail live preview of a load being composed on PostLoad — "how carriers
 *  will see your load". Reads the raw form state. */
export function LoadPreview({
  f, requirements, name,
}: {
  f: Record<string, string>;
  requirements: string[];
  name?: string;
}) {
  const num = (v?: string) => (v ? Number(v) : undefined);
  const rate =
    f.rate_type === "negotiable" ? "Negotiable" : f.rate ? rateLabel(num(f.rate) ?? 0, f.rate_type || "flat") : "";
  const specs: [string, string][] = [
    ["Equipment", labelize(f.equipment_type)],
    ["Weight", f.weight ? `${Number(f.weight).toLocaleString()} lb` : ""],
    ["Pickup", f.pickup_date ?? ""],
    ["Rate", rate],
  ];

  return (
    <Panel title="Live preview">
      <p className="-mt-1 text-xs text-muted">This is how carriers will see your load.</p>
      <div className="grid gap-3 rounded-lg border border-line bg-surface-2 p-4">
        <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted">
          <span className="rounded bg-verified-bg px-1.5 py-0.5 font-bold text-verified">New load</span>
          <span>Preview</span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 font-display text-base font-black tracking-tight">
          <Icon name="pin" className="h-4 w-4 text-verified" />
          <span className="truncate">{f.origin || "Origin"}</span>
          <span className="font-mono text-verified">››</span>
          <Icon name="pin" className="h-4 w-4 text-accent" />
          <span className="truncate">{f.destination || "Destination"}</span>
        </div>
        {f.trip_miles && <span className="font-mono text-xs text-muted">{Number(f.trip_miles).toLocaleString()} mi</span>}

        <div className="grid grid-cols-2 gap-y-2 border-y border-line py-2.5">
          {specs.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="grid gap-0.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted">{k}</span>
              <span className="font-mono text-xs font-bold text-ink">{v}</span>
            </div>
          ))}
        </div>

        <Tags items={[f.equipment_type, f.full_partial, f.category, ...requirements]} />

        {f.description && <p className="whitespace-pre-wrap text-xs text-muted line-clamp-4">{f.description}</p>}

        <div className="flex items-center gap-2 border-t border-line pt-2.5 text-xs">
          <Icon name="building" className="h-4 w-4 text-muted" />
          <span className="truncate font-bold">{name ?? "Your company"}</span>
          <VerifiedPill />
        </div>
      </div>
    </Panel>
  );
}
